import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createMove } from '../../store/moves';
import { fetchBuildingsUser } from '../../store/buildings';
import { fetchTrailersUser, getAvailableTrailers } from '../../store/trailers';

const MoveCreationModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [moveDetails, setMoveDetails] = useState({
    type: 'single',
    moves: [{ from: '', to: '', trailer: null }]
  });
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [selectedTrailers, setSelectedTrailers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moveType, setMoveType] = useState('single');
  const [trailerSearch, setTrailerSearch] = useState('');

  // Get current user from session
  const currentUser = useSelector(state => state.session.user);
  const selectedAccountId = useSelector(state => state.ui.selectedAccountId);
  const buildings = useSelector(state => Array.isArray(state.buildings) ? state.buildings : Object.values(state.buildings) || []);
  const availableTrailers = useSelector(state => state.trailers?.available ? Object.values(state.trailers.available) : []);

  // Define available locations - only Yard as standard location, buildings are handled separately
  const locations = ['Yard'];

  useEffect(() => {
    const loadData = async () => {
      if (!selectedAccountId) return;

      setIsLoading(true);
      setError(null);
      try {
        await Promise.all([
          dispatch(fetchTrailersUser(selectedAccountId)),
          dispatch(fetchBuildingsUser(selectedAccountId))
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, selectedAccountId]);

  const handleMoveTypeChange = (type) => {
    setMoveType(type);
    // Update move details based on type
    switch (type) {
      case 'single':
        setMoveDetails({
          type: 'single',
          moves: [{ from: '', to: '', trailer: null }]
        });
        break;
      case 'shuttle':
        setMoveDetails({
          type: 'shuttle',
          moves: [
            { from: '', to: '', trailer: null },
            { from: '', to: '', trailer: null }
          ]
        });
        break;
      case 'triangle':
        setMoveDetails({
          type: 'triangle',
          moves: [
            { from: '', to: '', trailer: null },
            { from: '', to: '', trailer: null },
            { from: '', to: '', trailer: null }
          ]
        });
        break;
      default:
        break;
    }
    setSelectedBuildings([]);
    setSelectedTrailers([]);
  };

  const handleTrailerSelect = (trailer, moveIndex) => {
    setMoveDetails(prev => ({
      ...prev,
      moves: prev.moves.map((move, idx) =>
        idx === moveIndex ? { ...move, trailer } : move
      )
    }));
  };

  const getAllLocations = () => {
    const locations = ['Yard', 'In Shop']; // Start with standard locations

    // Add building locations with door numbers
    buildings
      .filter(building => building.account_id === selectedAccountId)
      .forEach(building => {
        // Add doors for each building
        for (let i = 1; i <= building.building_door_number; i++) {
          locations.push(`${building.building_name}-${i}`);
        }
      });

    // Custom sort function to handle numeric parts correctly
    return locations.sort((a, b) => {
      // If either location is a standard location (Yard or In Shop), handle specially
      if (locations.includes(a) && !locations.includes(b)) return -1;
      if (!locations.includes(a) && locations.includes(b)) return 1;
      if (locations.includes(a) && locations.includes(b)) {
        return locations.indexOf(a) - locations.indexOf(b);
      }

      // Split locations into parts (e.g., "dc-1" becomes ["dc", "1"])
      const partsA = a.split('-');
      const partsB = b.split('-');

      // Compare building names first
      if (partsA[0] !== partsB[0]) {
        return partsA[0].localeCompare(partsB[0]);
      }

      // If building names are the same, compare door numbers numerically
      if (partsA.length > 1 && partsB.length > 1) {
        return parseInt(partsA[1]) - parseInt(partsB[1]);
      }

      return a.localeCompare(b);
    });
  };

  const handleLocationChange = (value, moveIndex, locationType) => {
    setMoveDetails(prev => ({
      ...prev,
      moves: prev.moves.map((move, idx) =>
        idx === moveIndex
          ? { ...move, [locationType]: value }
          : move
      )
    }));

    // If this is a "from" location change, trigger trailer loading
    if (locationType === 'from') {
      handleFromLocationSelect(value, moveIndex);
    }
  };

  const handleDoorSelection = (doorNumber, moveIndex, locationType) => {
    const building = selectedBuildings[moveIndex]?.[locationType];
    if (building) {
      setMoveDetails(prev => ({
        ...prev,
        moves: prev.moves.map((move, idx) =>
          idx === moveIndex
            ? { ...move, [locationType]: `${building.building_name}-${doorNumber}` }
            : move
        )
      }));
    }
  };

  const handleFromLocationSelect = async (location, moveIndex) => {
    if (!location || !selectedAccountId) {
      console.log('Missing required data:', { location, selectedAccountId });
      return;
    }

    try {
      console.log('Starting trailer fetch for:', {
        location,
        accountId: selectedAccountId,
        moveIndex
      });

      // Update the from location in moveDetails
      setMoveDetails(prev => ({
        ...prev,
        moves: prev.moves.map((move, idx) =>
          idx === moveIndex ? {
            ...move,
            from: location,
            trailer: null // Reset trailer when location changes
          } : move
        )
      }));

      // Clear trailer search when location changes
      setTrailerSearch('');

      // Fetch available trailers for the new location
      console.log('Dispatching getAvailableTrailers for:', location);
      const result = await dispatch(getAvailableTrailers(location, selectedAccountId));
      console.log('Available trailers result:', result);

    } catch (err) {
      console.error('Error loading trailers:', err);
      setError('Failed to load available trailers');
    }
  };

  const renderTrailerOptions = (moveIndex) => {
    const move = moveDetails.moves[moveIndex];

    if (!move.from) {
      return <p className="text-gray-500 italic">Please select a 'from' location first</p>;
    }

    if (!availableTrailers.length) {
      return <p className="text-gray-500 italic">No trailers available at this location</p>;
    }

    const filteredTrailers = availableTrailers.filter(trailer =>
      trailer.trailer_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
      (trailer.type && trailer.type.toLowerCase().includes(trailerSearch.toLowerCase()))
    );

    return (
      <div className="space-y-4">
        {/* Search bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search trailers..."
            value={trailerSearch}
            onChange={(e) => setTrailerSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Scrollable trailer list */}
        <div className="overflow-y-auto max-h-60 rounded-md border border-gray-200">
          <div className="grid grid-cols-1 gap-2 p-2">
            {filteredTrailers.map(trailer => (
              <div
                key={trailer.id}
                className={`p-3 border rounded-lg cursor-pointer ${
                  move.trailer?.id === trailer.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => handleTrailerSelect(trailer, moveIndex)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-xl">{trailer.trailer_number}</p>
                    <p className="text-sm text-gray-500">{trailer.type}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {trailer.is_loaded && (
                      <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded">Loaded</span>
                    )}
                    {trailer.yellow_tagged && (
                      <span className="px-2 py-1 text-sm bg-yellow-100 text-yellow-800 rounded">Yellow Tagged</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate all moves have trailers selected
    const missingTrailers = moveDetails.moves.some(move => !move.trailer);
    if (missingTrailers) {
      setError('Please select a trailer for each move');
      return;
    }

    // Validate locations are selected
    const missingLocations = moveDetails.moves.some(move => !move.from || !move.to);
    if (missingLocations) {
      setError('Please select both from and to locations for each move');
      return;
    }

    // Validate from and to locations are different
    const sameLocations = moveDetails.moves.some(move => move.from === move.to);
    if (sameLocations) {
      setError('From and To locations cannot be the same');
      return;
    }

    const moveData = {
      account_id: selectedAccountId,
      request_user_id: currentUser.id,
      move_details: {
        type: moveType,
        moves: moveDetails.moves.map(move => ({
          from: move.from,
          to: move.to,
          trailer: move.trailer.trailer_number
        }))
      },
      in_progress: false,
      is_cancel: false,
    };

    try {
      const result = await dispatch(createMove(moveData));
      if (result) {
        onClose();
        // Reset form
        setMoveDetails({
          type: 'single',
          moves: [{ from: '', to: '', trailer: null }]
        });
        setSelectedBuildings([]);
      }
    } catch (err) {
      console.error('Move creation error:', err);
      setError(err.message || 'Failed to create move. Please try again.');
    }
  };

  const LocationSelector = ({ moveIndex, locationType, value, onChange }) => (
    <div className="space-y-2">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 text-xl border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Select Location</option>
        {getAllLocations().map(location => (
          <option key={location} value={location}>
            {location}
          </option>
        ))}
      </select>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl">
            {/* Modal Header */}
            <div className="bg-white px-6 pb-6 pt-6 sm:p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-semibold text-gray-900">Create New Move</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-2xl text-gray-600">Loading...</div>
                </div>
              ) : error ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-2xl text-red-600">{error}</div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Move Type Selection */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-3">Move Type</label>
                    <div className="flex space-x-4">
                      {['single', 'shuttle', 'triangle'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleMoveTypeChange(type)}
                          className={`px-6 py-3 text-lg rounded-md ${
                            moveType === type
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Move Locations */}
                  <div className="space-y-8">
                    {moveDetails.moves.map((move, index) => (
                      <div key={index} className="space-y-6 p-6 bg-gray-50 rounded-lg">
                        <div>
                          <label className="block text-lg font-medium text-gray-700 mb-3">
                            Move {index + 1}
                          </label>
                          <div className="space-y-6">
                            <div>
                              <label className="block text-lg font-medium text-gray-700 mb-3">
                                From Location
                              </label>
                              <LocationSelector
                                moveIndex={index}
                                locationType="from"
                                value={move.from}
                                onChange={(value) => handleLocationChange(value, index, 'from')}
                              />
                            </div>
                            <div>
                              <label className="block text-lg font-medium text-gray-700 mb-3">
                                To Location
                              </label>
                              <LocationSelector
                                moveIndex={index}
                                locationType="to"
                                value={move.to}
                                onChange={(value) => handleLocationChange(value, index, 'to')}
                              />
                            </div>
                            {/* Trailer Selection for this move */}
                            <div>
                              <label className="block text-lg font-medium text-gray-700 mb-3">
                                Available Trailers for Move {index + 1}
                              </label>
                              {renderTrailerOptions(index)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-8">
                    <button
                      type="submit"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-6 py-3 text-lg font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-4 sm:w-auto"
                    >
                      Create Move
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-6 py-3 text-lg font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MoveCreationModal;
